import { useState, useCallback, useRef, useEffect } from 'react';
import { isAnimationEnabled, onAnimationPreferencesChanged } from '../utils/animationPreferences';

/**
 * Hook to manage sparkle trail animations for agent actions
 * Detects when the agent performs navigation and triggers delightful UI feedback
 */
export const useSparkleTrail = () => {
  const [activeTrail, setActiveTrail] = useState(null);
  const [isTrailActive, setIsTrailActive] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(() => isAnimationEnabled('sparkleTrails'));
  const sourceElementRef = useRef(null);
  const targetElementRef = useRef(null);

  // Listen for animation preference changes
  useEffect(() => {
    const cleanup = onAnimationPreferencesChanged((preferences) => {
      setAnimationsEnabled(preferences.sparkleTrails);
    });
    return cleanup;
  }, []);

  // Trigger sparkle trail from agent message to target
  const triggerSparkleTrail = useCallback((actionType, sourceElement, targetElement) => {
    // Check if animations are enabled
    if (!animationsEnabled) {
      console.log(`🎇 Sparkle trail skipped (animations disabled): ${actionType}`);
      return;
    }

    // Set source and target elements
    sourceElementRef.current = sourceElement;
    targetElementRef.current = targetElement;
    
    // Start the trail animation
    setActiveTrail({
      actionType,
      startTime: Date.now(),
      id: `trail-${Date.now()}`
    });
    setIsTrailActive(true);
    
    console.log(`🎇 Sparkle trail triggered: ${actionType}`);
  }, [animationsEnabled]);

  // Complete the trail animation
  const completeTrail = useCallback(() => {
    setIsTrailActive(false);
    setActiveTrail(null);
    sourceElementRef.current = null;
    targetElementRef.current = null;
  }, []);

  // Auto-detect agent actions and trigger sparkles
  const detectAgentAction = useCallback((agentResponse) => {
    if (!agentResponse?.action) return;
    
    // Check if animations are enabled
    if (!animationsEnabled) {
      console.log('🎇 Agent action detected but animations disabled:', agentResponse.action);
      return;
    }

    console.log('🎇 Agent action detected:', agentResponse.action);

    // Find the last agent message element
    const agentMessages = document.querySelectorAll('[data-role="assistant"]');
    const lastAgentMessage = agentMessages[agentMessages.length - 1];
    
    console.log('🎇 Found agent messages:', agentMessages.length, 'Last message:', lastAgentMessage);
    
    if (!lastAgentMessage) {
      console.warn('🎇 No agent message element found for sparkles');
      return;
    }

    let targetSelectors = [];
    let actionType = 'navigate';

    // Determine target element based on action type with multiple fallbacks
    switch (agentResponse.action) {
      case 'navigate_to_analysis':
        targetSelectors = [
          '#analysis-results-table',
          '[data-testid="analysis-table"]', 
          'table',
          '.analysis-container',
          '[class*="analysis"]',
          'main'
        ];
        actionType = 'analyze';
        break;
        
      case 'navigate_to_page':
        targetSelectors = [
          '[data-page-analysis]',
          '.page-analysis-header',
          '[class*="page-analysis"]',
          'main',
          '.main-content'
        ];
        actionType = 'page';
        break;
        
      case 'trigger_analysis':
        targetSelectors = [
          '.analysis-loading',
          '#analysis-results-table',
          '[data-testid="analysis-table"]',
          'main'
        ];
        actionType = 'analyze';
        break;
        
      default:
        targetSelectors = ['main', '.main-content', 'body'];
        actionType = 'navigate';
    }

    // Try to find target element with retries for DOM updates
    const findTargetWithRetry = (attempt = 0) => {
      const maxAttempts = 5;
      const delay = Math.min(100 * Math.pow(1.5, attempt), 1000); // Progressive delay
      
      let targetElement = null;
      for (const selector of targetSelectors) {
        targetElement = document.querySelector(selector);
        if (targetElement) break;
      }
      
      if (targetElement) {
        // Success! Trigger sparkle trail
        console.log('🎇 Found target element:', targetElement);
        triggerSparkleTrail(actionType, lastAgentMessage, targetElement);
      } else if (attempt < maxAttempts - 1) {
        // Retry with increasing delay
        console.log(`🎇 Target not found, retrying in ${delay}ms...`);
        setTimeout(() => findTargetWithRetry(attempt + 1), delay);
      } else {
        // Final fallback - use main or body
        const fallbackTarget = document.querySelector('main') || document.querySelector('body');
        console.log('🎇 Using fallback target:', fallbackTarget);
        if (fallbackTarget) {
          triggerSparkleTrail(actionType, lastAgentMessage, fallbackTarget);
        }
      }
    };

    // Start the search immediately
    findTargetWithRetry();
  }, [triggerSparkleTrail, animationsEnabled]);

  // Manual trigger for specific elements
  const triggerSparkleFromTo = useCallback((sourceSelector, targetSelector, actionType = 'navigate') => {
    const sourceElement = typeof sourceSelector === 'string' 
      ? document.querySelector(sourceSelector) 
      : sourceSelector;
    const targetElement = typeof targetSelector === 'string' 
      ? document.querySelector(targetSelector) 
      : targetSelector;
    
    if (sourceElement && targetElement) {
      triggerSparkleTrail(actionType, sourceElement, targetElement);
    } else {
      console.warn('SparkleTrail: Could not find source or target element', {
        sourceElement,
        targetElement,
        sourceSelector,
        targetSelector
      });
    }
  }, [triggerSparkleTrail]);

  // Add destination highlight effect
  const highlightDestination = useCallback((element, duration = 2000) => {
    if (!element) return;
    
    // Add highlight class
    element.classList.add('sparkle-destination-highlight');
    
    // Remove after duration
    setTimeout(() => {
      element.classList.remove('sparkle-destination-highlight');
    }, duration);
  }, []);

  return {
    // State
    isTrailActive,
    activeTrail,
    sourceElement: sourceElementRef.current,
    targetElement: targetElementRef.current,
    animationsEnabled,
    
    // Actions
    triggerSparkleTrail,
    triggerSparkleFromTo,
    completeTrail,
    detectAgentAction,
    highlightDestination
  };
};

// CSS classes for destination highlighting
export const sparkleTrailStyles = `
  .sparkle-destination-highlight {
    animation: sparkle-glow 2s ease-out;
    transform-origin: center;
  }
  
  @keyframes sparkle-glow {
    0% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
      transform: scale(1);
    }
    25% {
      box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.3);
      transform: scale(1.02);
    }
    50% {
      box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.1);
      transform: scale(1.01);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
      transform: scale(1);
    }
  }
`;

export default useSparkleTrail;
